import cuid from "cuid";
import { filter, find, get, map } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  ATTACHMENT_TYPES,
  DOCUMENTS_TYPES,
  KEYS,
  RELATIONSHIP,
  PROCESS_TYPES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

export const DOCUMENTS_TYPE = {
  socialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
};

const ChildrenJustification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState({ includedChildren: [] });
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_AMELLIS.CHILDREN_JUSTIFICATIONS", {
    returnObjects: true,
  });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const effectiveDate =
    get(data, "subscription.contract.issuanceDate") ||
    get(project, "contract.issuanceDate") ||
    get(project, "fields.effectiveDate");

  const children = get(project, "contact.relatedPersons", []).filter(
    ({ relationship }) => relationship === RELATIONSHIP.CHILD,
  );

  const includedChildren = children
    .map((child) => ({
      id: get(child, "identity.id") ?? child.id,
      userId: get(child, "identity.user.id"),
      age: moment(effectiveDate).diff(
        get(child, "identity.user.birthDate"),
        "years",
      ),
      identity: get(child, "identity"),
    }))
    .filter((c) => c.userId);

  const childrenUserIds = includedChildren.map((child) => child.userId);

  const supportingDocuments = useMemo(() => {
    const profile = get(data, "subscription.project.contact.fields.profile");
    return get(
      find(
        get(data, "subscription.insurancePlan.fields.supportingDocuments"),
        ({ profiles, processType }) =>
          processType === PROCESS_TYPES.B2C && profiles.includes(profile),
      ),
      "attachments",
      [],
    );
  }, [data]);

  // Get adherent and spouse user IDs
  const adherentUserId =
    get(data, "subscription.project.contact.user.id") ||
    get(project, "contact.user.id");

  const spouse = get(project, "contact.relatedPersons", []).find(
    ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
  );
  const spouseUserId = get(spouse, "identity.user.id");

  const { loading: attachmentLoading, refetch: refetchAttachments } = useQuery(
    graphql.queries.ATTACHMENTS,
    {
      fetchPolicy: "no-cache",
      variables: {
        where: {
          AND: [
            {
              subscription: { id: subscriptionId },
            },
            {
              // Filter by all children's user.id
              OR: childrenUserIds.map((id) => ({ user: { id } })),
            },
          ],
        },
        isIn: {
          type: [ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE],
        },
      },
      skip: childrenUserIds.length === 0,
      onCompleted: ({ attachments: { data: attachmentsList } }) => {
        const initialVals = {};
        includedChildren.forEach(({ userId, age }) => {
          // Filter attachments by user.id to ensure each child only sees their own documents
          const childAttachments = attachmentsList.filter((att) => {
            const attachmentUserId = get(att, "user.id") || get(att, "userId");
            return attachmentUserId === userId;
          });

          initialVals[`socialSecurityCertificate_${userId}`] = map(
            filter(
              childAttachments,
              (att) =>
                att.type ===
                ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          );
        });

        setInitialValues({ ...initialVals, includedChildren });
        setLoading(false);
      },
    },
  );

  const [addAttachment] = useMutation(CREATE_ATTACHMENT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [getAllAttachments] = useLazyQuery(graphql.queries.ATTACHMENTS);

  const onSubmit = async ({ includedChildren, ...documents }) => {
    setLoading(true);
    const documentsToUpload = [];

    includedChildren.forEach(({ userId }) => {
      const inputKey = `socialSecurityCertificate_${userId}`;
      const attachments = documents[inputKey] || [];
      attachments
        ?.filter((e) => !e.status)
        .forEach(({ name, contentType, base64 }) => {
          documentsToUpload.push({
            id: cuid(),
            subscription: { id: subscriptionId },
            user: { id: userId },
            name,
            content: base64,
            contentType,
            type: ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
          });
        });
    });

    await Promise.all(
      documentsToUpload.map((document) =>
        addAttachment({ variables: { data: document } }),
      ),
    );

    const allUserIds = [
      adherentUserId,
      spouseUserId,
      ...childrenUserIds,
    ].filter(Boolean);

    if (allUserIds.length > 0) {
      await getAllAttachments({
        fetchPolicy: "no-cache",
        variables: {
          where: {
            AND: [
              {
                subscription: { id: subscriptionId },
              },
              {
                OR: allUserIds.map((id) => ({ user: { id } })),
              },
            ],
          },
          isIn: {
            type: [
              DOCUMENTS_TYPES.CIN,
              DOCUMENTS_TYPES.PAYMENT_ACCOUNT,
              DOCUMENTS_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
              DOCUMENTS_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
              DOCUMENTS_TYPES.SOCIAL_SECURITY_CERTIFICATE,
              DOCUMENTS_TYPES.PAY_SLIP,
              DOCUMENTS_TYPES.ELECTRONIC_SIGNATURE_ELIGIBILITY,
              ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
              ATTACHMENT_TYPES.OTHER,
            ],
          },
        },
      });
    }

    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });

    return onNext({
      payload: {},
      actionsTemplate: { subscriptionId },
    });
    setLoading(false);
  };

  const downloadFile = async (id) => {
    await downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
      },
    });
  };

  const handleFileRemove = (fieldName) => async (item) => {
    const fileId = item?.uid || item?.id;

    // If file is already uploaded (has an id and doesn't contain "AUTO"), delete it
    if (fileId && typeof fileId === "string" && !fileId.includes("AUTO")) {
      try {
        await deleteAttachment({
          variables: {
            where: { id: fileId },
          },
        });
        // Refetch attachments to update the UI
        if (refetchAttachments) {
          await refetchAttachments();
        }
      } catch (error) {
        console.error("Error deleting attachment:", error);
      }
    }

    return fieldName;
  };

  if (attachmentLoading || !initialValues) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showProfile={true}
        showCart={true}
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
      />
      <View
        onSubmit={onSubmit}
        initialValues={{ ...initialValues, includedChildren }}
        downloadFile={downloadFile}
        onBack={onBack}
        translate={TRANSLATION}
        onFileRemove={handleFileRemove}
        supportingDocuments={supportingDocuments}
        subscription={get(data, "subscription")}
        project={project}
      />
    </>
  );
};

export default ChildrenJustification;
